/**
 * SuperAdminPage - 消費者プロダクトの運営ダッシュボード (/admin)
 *
 * 診断・チャット・24type 分布など、稼働中の消費者プロダクトの指標を表示。
 * B2B (MRR・企業) は「企業 (B2B)」タブへ退避。
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import adminService from '../../services/adminService';
import comprehensiveTypes from '../../data/comprehensive_types.json';
import AdminLayout from './AdminLayout';
import TrendChart from '../../components/admin/TrendChart';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 36px;

  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const StatCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
`;

const StatLabel = styled.div`
  font-size: 0.76rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.text};
  font-family: ${(p) => p.theme.fonts?.mono};
`;

const StatSub = styled.div`
  font-size: 0.74rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 4px;
`;

const Section = styled.section`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  padding: 22px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 4px 0;
`;

const SectionSub = styled.div`
  font-size: 0.76rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-bottom: 16px;
`;

/* ---- horizontal breakdown bars ---- */
const BreakRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 0;
`;

const BreakLabel = styled.div`
  width: 168px;
  flex-shrink: 0;
  font-size: 0.86rem;
  color: ${(p) => p.theme.colors.text};
  display: flex;
  align-items: baseline;
  gap: 7px;

  @media (max-width: 480px) { width: 120px; }
`;

const BreakSub = styled.span`
  font-size: 0.72rem;
  color: ${(p) => p.theme.colors.textMuted};
`;

const BreakBarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: ${(p) => p.theme.colors.bgTertiary};
  border-radius: 4px;
  overflow: hidden;
`;

const BreakBarFill = styled.div`
  height: 100%;
  width: ${(p) => Math.max(p.$pct, 1)}%;
  background: ${(p) => p.$accent || p.theme.colors.primary};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const BreakCount = styled.div`
  width: 48px;
  flex-shrink: 0;
  text-align: right;
  font-family: ${(p) => p.theme.fonts?.mono};
  font-weight: 700;
  font-size: 0.86rem;
  color: ${(p) => p.theme.colors.text};
`;

const EmptyNote = styled.div`
  font-size: 0.82rem;
  color: ${(p) => p.theme.colors.textMuted};
  padding: 18px 0;
  text-align: center;
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg,
    ${(p) => p.theme.colors.bgTertiary} 25%,
    ${(p) => p.theme.colors.bgSecondary} 50%,
    ${(p) => p.theme.colors.bgTertiary} 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: 8px;
  height: ${(p) => p.$h || '20px'};
  width: ${(p) => p.$w || '100%'};
  margin-bottom: ${(p) => p.$mb || '0'};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

function SuperAdminPage() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const consumer = await adminService.getConsumerStats();
      setStats(consumer);
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const typeName = (code) => comprehensiveTypes[code]?.name || code;

  if (loading) {
    return (
      <AdminLayout active="dashboard">
        <StatsGrid>
          {[0, 1, 2, 3].map((i) => (
            <StatCard key={i}>
              <SkeletonBlock $h="14px" $w="60%" $mb="8px" />
              <SkeletonBlock $h="30px" $w="70px" />
            </StatCard>
          ))}
        </StatsGrid>
        <SkeletonBlock $h="200px" $w="100%" $mb="24px" />
        <SkeletonBlock $h="240px" $w="100%" />
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout active="dashboard">
        <ErrorContainer>
          <h2 style={{ color: '#EF4444' }}>{t('データ取得エラー', 'Error')}</h2>
          <p style={{ color: '#8888a8' }}>{error}</p>
        </ErrorContainer>
      </AdminLayout>
    );
  }

  const typeMax = Math.max(1, ...(stats?.type_distribution || []).map((x) => x.count));

  return (
    <AdminLayout active="dashboard">
      {/* 消費者プロダクトの主要指標 */}
      <StatsGrid>
        <StatCard>
          <StatLabel>{t('総診断数', 'Diagnoses')}</StatLabel>
          <StatValue>{(stats?.total_diagnoses || 0).toLocaleString()}</StatValue>
          <StatSub>{t('累計', 'all time')}</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>{t('ユーザー数', 'Users')}</StatLabel>
          <StatValue>{(stats?.total_users || 0).toLocaleString()}</StatValue>
          <StatSub>{t(`新規 +${stats?.new_users_30d || 0} (30日)`, `+${stats?.new_users_30d || 0} new (30d)`)}</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>{t('チャット会話数', 'Conversations')}</StatLabel>
          <StatValue>{(stats?.total_conversations || 0).toLocaleString()}</StatValue>
          <StatSub>{t('AIチャット', 'AI chat')}</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>{t('チャットメッセージ数', 'Chat Messages')}</StatLabel>
          <StatValue>{(stats?.total_chat_messages || 0).toLocaleString()}</StatValue>
          <StatSub>{t('累計', 'all time')}</StatSub>
        </StatCard>
      </StatsGrid>

      {/* 診断数の推移 */}
      <Section>
        <SectionTitle>{t('診断数の推移', 'Diagnoses Trend')}</SectionTitle>
        <SectionSub>{t('過去 30 日', 'Last 30 days')}</SectionSub>
        {stats?.diagnoses_daily?.length ? (
          <TrendChart data={stats.diagnoses_daily} />
        ) : (
          <EmptyNote>{t('データがありません', 'No data')}</EmptyNote>
        )}
      </Section>

      {/* 新規ユーザーの推移 */}
      <Section>
        <SectionTitle>{t('新規ユーザー登録の推移', 'New Users Trend')}</SectionTitle>
        <SectionSub>{t('過去 30 日', 'Last 30 days')}</SectionSub>
        {stats?.users_daily?.length ? (
          <TrendChart data={stats.users_daily} />
        ) : (
          <EmptyNote>{t('データがありません', 'No data')}</EmptyNote>
        )}
      </Section>

      {/* 24 type 分布 */}
      <Section>
        <SectionTitle>{t('24 type の分布', '24-Type Distribution')}</SectionTitle>
        <SectionSub>{t('診断結果の type 別件数 (current 優先)', 'Diagnoses by type')}</SectionSub>
        {stats?.type_distribution?.length ? (
          stats.type_distribution.map((x) => (
            <BreakRow key={x.type_code}>
              <BreakLabel>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: '0.8rem' }}>
                  {x.type_code}
                </span>
                <BreakSub>{typeName(x.type_code)}</BreakSub>
              </BreakLabel>
              <BreakBarTrack>
                <BreakBarFill $pct={(x.count / typeMax) * 100} />
              </BreakBarTrack>
              <BreakCount>{x.count}</BreakCount>
            </BreakRow>
          ))
        ) : (
          <EmptyNote>{t('診断データがまだありません', 'No diagnosis data yet')}</EmptyNote>
        )}
      </Section>
    </AdminLayout>
  );
}

export default SuperAdminPage;
